import { useState } from 'react'
import { useStudentCurriculum } from '@/hooks/useStudentCurriculum'
import { SecureAssetViewer } from '@/components/curriculum/SecureAssetViewer'
import { AssetThumbnail } from '@/components/curriculum/AssetThumbnail'
import { Icon, ICONS } from '@/components/ui/Icon'

const cardStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14,
  padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12,
}

export default function StudentCourse() {
  const { lessons, loading, error } = useStudentCurriculum()
  const [open, setOpen] = useState<{ lessonId: string; assetId: string; type: 'pdf' | 'video'; title: string } | null>(null)

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>Your lessons</div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 24, maxWidth: 680 }}>
        Your instructor assigns lessons one at a time. Review the PDF, then complete the in-car portion with your
        instructor — the lesson video unlocks for revision once they mark it done.
      </div>

      {error && <div className='banner-error' style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'rgba(27,42,74,0.4)' }}>Loading…</div>
      ) : lessons.length === 0 ? (
        <div style={{ background: '#fff', border: '1px dashed rgba(27,42,74,0.15)', borderRadius: 14, padding: '40px 28px', textAlign: 'center', color: 'rgba(27,42,74,0.45)' }}>
          No lessons have been assigned yet. Your instructor will assign them as you progress.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 320px))', gap: 14 }}>
          {lessons.map(l => (
            <div key={l.assignmentId} style={cardStyle}>
              <div>
                <div style={{ font: '600 14.5px Inter, sans-serif', color: '#1B2A4A' }}>{l.lessonTitle}</div>
                {l.lessonDescription && (
                  <div style={{ font: '400 12.5px/1.5 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 4 }}>{l.lessonDescription}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {l.pdfAssetId && (
                  <AssetThumbnail
                    assetId={l.pdfAssetId} assetType='pdf' title={`${l.lessonTitle} — PDF`}
                    onClick={() => setOpen({ lessonId: l.lessonId, assetId: l.pdfAssetId!, type: 'pdf', title: l.lessonTitle })}
                  />
                )}
                {l.videoAssetId ? (
                  <AssetThumbnail
                    assetId={l.videoAssetId} assetType='video' title={`${l.lessonTitle} — Video`}
                    onClick={() => setOpen({ lessonId: l.lessonId, assetId: l.videoAssetId!, type: 'video', title: l.lessonTitle })}
                  />
                ) : (
                  <div style={{
                    width: 140, height: 90, borderRadius: 8, flexShrink: 0,
                    background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}>
                    <Icon path={ICONS.lock} size={16} />
                    <span style={{ font: '500 10px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', textAlign: 'center', padding: '0 8px' }}>
                      Unlocks after in-car lesson
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}
          onClick={() => setOpen(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 16, maxWidth: 820, width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>{open.title}</div>
              <button onClick={() => setOpen(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'rgba(27,42,74,0.5)' }}>✕</button>
            </div>
            <SecureAssetViewer assetId={open.assetId} assetType={open.type} title={open.title} />
          </div>
        </div>
      )}
    </div>
  )
}
